import { useEffect, useState } from 'react';
import { EXPERIMENT_FLAGS, type OnboardingVariant } from '@scio3/shared-types';
import { isPosthogEnabled, posthog } from '@/lib/posthog';
import { useAuthStore } from '@/stores/auth-store';
import { readQaOverride } from '../utils/qa-override';

/**
 * Safety net for the pathological "SDK loaded but never answers" case. Kept
 * generous so a slow-but-working flag load is never pre-empted by a premature
 * control answer (which would flash the survey over the screens for treatment).
 */
const FLAG_SAFETY_TIMEOUT_MS = 8000;

// ── Per-user variant cache ────────────────────────────────────────────────────
//
// First resolution is read from PostHog (against the IDENTIFIED user) and saved
// to localStorage; every later load returns the cached value WITHOUT calling
// PostHog — so the assignment is sticky and can never flip. Keyed by userId to
// avoid one user's variant bleeding to the next on a shared computer.
const CACHE_KEY = (userId: string) => `scio3b-split:variant:${userId}`;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7-day backstop so a kill switch eventually propagates

function readCachedVariant(userId: string | null | undefined): OnboardingVariant | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { variant?: unknown; ts?: unknown };
    if (
      (parsed.variant === 'scio3a' || parsed.variant === 'scio3b') &&
      typeof parsed.ts === 'number' &&
      Date.now() - parsed.ts <= CACHE_TTL_MS
    ) {
      return parsed.variant;
    }
  } catch {
    // malformed / unavailable — fall through to re-resolve
  }
  return null;
}

function writeCachedVariant(userId: string, variant: OnboardingVariant): void {
  try {
    localStorage.setItem(CACHE_KEY(userId), JSON.stringify({ variant, ts: Date.now() }));
  } catch {
    // localStorage unavailable / quota — skip; we simply re-resolve next time.
  }
}

/**
 * Resolve the user's variant for the scio3b-split A/B test — cache-first.
 *
 * Order of precedence: QA override → localStorage cache → the first IDENTIFIED
 * PostHog answer. Once a value is locked it never flips for the session.
 *
 * - **Cache hit** → return it immediately, no PostHog call (this is what makes
 *   the assignment sticky across loads / sessions).
 * - **Cache miss** → wait for an IDENTIFIED PostHog answer (`_isIdentified()` and
 *   `distinct_id === userId`), so we never cache or expose the anonymous bucket —
 *   that anonymous→identified rebucket was the scio3a↔scio3b flip. The single
 *   `getFeatureFlag()` call is the one `$feature_flag_called` exposure.
 * - No SDK / 8s timeout → control (`scio3a`), never cached.
 *
 * Resolution lives here (not in AuthProvider) so it runs during the signup flow
 * too — AuthProvider's `loadUserContext` early-returns while `signupInProgress`.
 */
export function useOnboardingVariant(): {
  variant: OnboardingVariant;
  isLoading: boolean;
} {
  // Read override once on mount — subsequent ?force_variant= changes only take
  // effect on full page reload, matching how PostHog assigns variants.
  const [override] = useState<OnboardingVariant | null>(() => readQaOverride());
  const userId = useAuthStore((s) => s.me?._id);

  // Sticky, resolved-once value. Seeded synchronously from override / cache so a
  // returning user never sees the loading skeleton.
  const [locked, setLocked] = useState<OnboardingVariant | null>(
    () => override ?? readCachedVariant(useAuthStore.getState().me?._id),
  );

  useEffect(() => {
    if (locked) return; // already sticky (override / cache / earlier resolve)

    // userId can arrive after first render (during signup) — re-check the cache.
    const cached = readCachedVariant(userId);
    if (cached) {
      setLocked(cached);
      return;
    }

    // No SDK (no key / embedded iframe) → control; don't sit on the skeleton.
    if (!isPosthogEnabled) {
      setLocked('scio3a');
      return;
    }

    let done = false;
    // Accept ONLY an identified answer, so we never cache/expose the anonymous
    // bucket. The getFeatureFlag() read here is the single session exposure.
    const accept = () => {
      if (done) return;
      if (!posthog._isIdentified?.() || !userId || posthog.get_distinct_id?.() !== userId) return;
      const value =
        posthog.getFeatureFlag(EXPERIMENT_FLAGS.SCIO3B_SPLIT) === 'scio3b' ? 'scio3b' : 'scio3a';
      done = true;
      writeCachedVariant(userId, value);
      setLocked(value);
    };

    accept(); // flags may already be loaded + identified
    const unsub = posthog.onFeatureFlags?.((flags, variants, info) => {
      // Diagnostic: visible in DevTools so you can tell whether PostHog answered,
      // what variant it assigned, and whether the fire was identified (an
      // anonymous fire is intentionally ignored by `accept`).
      console.warn(`[experiment] ${EXPERIMENT_FLAGS.SCIO3B_SPLIT}: PostHog responded.`, {
        assignedVariant: variants?.[EXPERIMENT_FLAGS.SCIO3B_SPLIT] ?? '(none — control)',
        identified: posthog._isIdentified?.(),
        distinctId: posthog.get_distinct_id?.(),
        expectedUserId: userId,
        totalActiveFlags: flags.length,
        errorsLoading: info?.errorsLoading,
      });
      accept();
    });

    const timeoutId = window.setTimeout(() => {
      if (done) return;
      done = true;
      console.error(
        `[experiment] ${EXPERIMENT_FLAGS.SCIO3B_SPLIT}: no identified PostHog answer within ${FLAG_SAFETY_TIMEOUT_MS}ms — falling back to control (scio3a). ` +
          `Check that VITE_POSTHOG_KEY is set and VITE_POSTHOG_API_HOST is reachable.`,
      );
      setLocked('scio3a');
    }, FLAG_SAFETY_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
      unsub?.();
    };
  }, [locked, userId]);

  if (override) return { variant: override, isLoading: false };
  if (locked) return { variant: locked, isLoading: false };
  // Still resolving the first identified answer → keep the skeleton.
  return { variant: 'scio3a', isLoading: true };
}

/**
 * Stable boolean check — useful for `if`-style branching outside JSX without
 * also pulling `isLoading`. During the loading window, returns `false` so
 * callers don't accidentally render scio3b UI before the variant resolves.
 */
export function useIsScio3bVariant(): boolean {
  const { variant, isLoading } = useOnboardingVariant();
  return !isLoading && variant === 'scio3b';
}

/**
 * One-shot side-effect: registers `onboarding_variant` as a PostHog person
 * property once the variant resolves, so every subsequent event auto-attaches
 * it without callers needing to thread the prop through.
 *
 * Mount once near the variant decision point (e.g. inside `OnboardingRouter`).
 */
export function useRegisterVariantProperty(variant: OnboardingVariant, isLoading: boolean): void {
  useEffect(() => {
    if (isLoading) return;
    // Lazy-import the singleton so we don't pull posthog into modules that only
    // need the variant value (e.g. tests).
    import('@/lib/posthog')
      .then(({ posthog }) => {
        posthog.register({ onboarding_variant: variant });
      })
      .catch(() => {
        // Posthog optional — swallow.
      });
  }, [variant, isLoading]);
}
